import { masterpassSdkUrl } from 'Config';
import { MasterpassJwtClaims } from 'Models/MasterpassSdkModels';
import getActiveLanguageForMasterPass from './MasterPassLanguageHelper';

/**
 * Masterpass Javascript SDK Web v1.0.7 kurulum katmanı.
 *
 * Eski MFS SDK'sında kurulum tek satırdı (`MFS.setClientId('34705878')`) ve
 * kimlik doğrulama işlem başına alınan TLV token ile yapılıyordu. Yeni SDK'da
 * kurulum iki parçalıdır:
 *
 *  1. Uygulama açılışında  → `initMasterpassSdk()`      (adres + dil, oturumdan bağımsız)
 *  2. Oturum açıldığında   → `authorizeMasterpassSdk()` (JWT + merchantId)
 *
 * JWT'siz hiçbir servis çağrılamaz; SDK isteği atar ama backend 401 döner.
 */

/** SDK script'i (`public/js/masterpass-javascript-sdk-web.min.js`) yüklendi mi? */
export const isMasterpassSdkLoaded = (): boolean => typeof window !== 'undefined' && window.Masterpass !== undefined;

/**
 * SDK'yı oturumdan bağımsız olarak hazırlar: servis kökü ve dil.
 * Uygulama açılışında bir kez çağrılması yeterlidir.
 */
export const initMasterpassSdk = (): boolean => {
  if (!isMasterpassSdkLoaded()) {
    return false;
  }

  window.Masterpass.setEndpoint(masterpassSdkUrl);
  window.Masterpass.setLanguage(getActiveLanguageForMasterPass());

  return true;
};

/**
 * JWT'yi çözer. Bozuk/eksik token'da `undefined` döner — `Utils.decodeJwt`
 * geçersiz girdide exception fırlattığı için burada yakalanır.
 */
export const decodeMasterpassJwt = (jwt?: string): MasterpassJwtClaims | undefined => {
  if (!jwt || !isMasterpassSdkLoaded()) {
    return undefined;
  }

  try {
    return window.Utils.decodeJwt(jwt);
  } catch (error) {
    console.error('Masterpass JWT çözümlenemedi:', error);
    return undefined;
  }
};

/**
 * SDK'yı verilen JWT ile yetkilendirir: `Authorization` header'ını ve her istek
 * gövdesine eklenen `merchantId` alanını kurar.
 *
 * Eski SDK'daki sabit `clientId` (test `34705878` / prod `34706580`) değerleri
 * artık kodda gömülü tutulmaz; üye işyeri kimliği backend'den gelir.
 *
 * @param jwt `RI.OnGenerateJwtoken` cevabındaki `Token` (`"Bearer …"` ön ekli
 *   olabilir; SDK ön eki kendisi ayıklar).
 * @param merchantId Servisin döndürdüğü `MpMerchantId`. Verilmezse JWT'nin
 *   `MerchantId` claim'ine düşülür — ikisi aynı değeri taşır, servisten geleni
 *   tercih ediyoruz çünkü claim şeması değişse bile bozulmaz.
 * @returns Çözülen JWT claim'leri; token geçersizse `undefined`.
 */
export const authorizeMasterpassSdk = (jwt?: string, merchantId?: string): MasterpassJwtClaims | undefined => {
  if (!jwt || !isMasterpassSdkLoaded()) {
    return undefined;
  }

  const claims = decodeMasterpassJwt(jwt);
  if (!claims) {
    return undefined;
  }

  window.Masterpass.setToken(jwt);

  const resolvedMerchantId = merchantId || claims.MerchantId;
  if (resolvedMerchantId) {
    window.Masterpass.setMerchantId(resolvedMerchantId);
  }

  return claims;
};

/**
 * SDK'nın çok adımlı akış durumu (`window.FlowDirectable`).
 *
 * OTP/CVV/RTA doğrulaması, kendisinden önceki isteğin 202 cevabında SDK'nın
 * buraya yazdığı `uri` ve `token` değerlerine dayanır. Eski SDK'daki
 * `MFS.getLastToken()` karşılığı `flowDirectable().token`'dır.
 */
export const flowDirectable = () => window.FlowDirectable;
